import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Trash2, Plus, Minus, IndianRupee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { shopAPI } from '@/lib/api';

export default function Cart() {
  const { shopSlug } = useParams();
  const navigate = useNavigate();
  const { cart, updateQuantity, removeFromCart, getTotal } = useCart();

  const [shop, setShop] = useState(null);
  const [shopLoading, setShopLoading] = useState(true);

  useEffect(() => {
    fetchShop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSlug]);

  const fetchShop = async () => {
    try {
      setShopLoading(true);
      const response = await shopAPI.getShop(shopSlug);
      setShop(response.data?.seller || null);
    } catch (error) {
      console.error('Failed to fetch shop:', error);
      setShop(null);
    } finally {
      setShopLoading(false);
    }
  };

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity < 1) return;
    updateQuantity(itemId, newQuantity);
  };

  const subtotal = useMemo(() => Number(getTotal() || 0), [getTotal, cart]);

  const pricing = useMemo(() => {
    const deliveryAmount = Number(shop?.delivery_amount ?? shop?.delivery_charge ?? 0);
    const freeFrom = Number(
      shop?.free_delivery_start_range ??
        shop?.free_delivery_min_amount ??
        0
    );

    const isFreeDelivery = freeFrom > 0 && subtotal >= freeFrom;
    const deliveryFee = isFreeDelivery ? 0 : deliveryAmount;
    const grandTotal = Number(subtotal + deliveryFee);

    return {
      deliveryAmount,
      freeFrom,
      isFreeDelivery,
      deliveryFee,
      grandTotal
    };
  }, [shop, subtotal]);

  const { deliveryAmount, freeFrom, isFreeDelivery, deliveryFee, grandTotal } = pricing;

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <ShoppingBag className="h-16 w-16 mx-auto text-muted-foreground" />
          <h2 className="text-2xl font-heading font-bold">Your cart is empty</h2>
          <p className="text-muted-foreground">Add some products to continue shopping</p>
          <Button onClick={() => navigate(`/shop/${shopSlug}`)} className="rounded-full">
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="cart-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to={`/shop/${shopSlug}`}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Continue Shopping</span>
          </Link>
          <div className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            <span className="font-heading font-bold">Cart ({cart.length})</span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <Card key={item.id} className="border-border/50">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center overflow-hidden">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <ShoppingBag className="h-8 w-8 text-muted-foreground" />
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex justify-between items-start gap-4">
                        <div>
                          <h3 className="font-heading font-bold">{item.name}</h3>
                          <p className="text-sm text-muted-foreground">{item.category_name}</p>
                          <p className="text-lg font-heading font-bold text-primary mt-1">
                            ₹{item.final_price}
                          </p>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            removeFromCart(item.id);
                            toast.success('Removed from cart');
                          }}
                          className="text-destructive hover:text-destructive"
                          data-testid="remove-item-btn"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-3 mt-4">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          data-testid="decrease-qty-btn"
                        >
                          <Minus className="h-4 w-4" />
                        </Button>

                        <span className="w-10 text-center font-medium">{item.quantity}</span>

                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          data-testid="increase-qty-btn"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>

                        <div className="ml-auto text-sm text-muted-foreground">
                          Subtotal: ₹{item.final_price * item.quantity}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="border-border/50 sticky top-24">
              <CardHeader>
                <CardTitle className="font-heading">Order Summary</CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm">
                  {cart.map((item) => (
                    <div key={item.id} className="flex justify-between">
                      <span className="text-muted-foreground">
                        {item.name} × {item.quantity}
                      </span>
                      <span>₹{item.final_price * item.quantity}</span>
                    </div>
                  ))}
                </div>

                <Separator />

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>₹{subtotal}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Delivery {freeFrom > 0 ? `(Free from ₹${freeFrom})` : ''}
                    </span>
                    <span>{shopLoading ? 'Loading...' : deliveryFee === 0 ? '₹0' : `₹${deliveryFee}`}</span>
                  </div>

                  {!shopLoading && freeFrom > 0 && !isFreeDelivery && deliveryAmount > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Add ₹{Math.max(0, freeFrom - subtotal)} more for free delivery.
                    </p>
                  )}

                  {!shopLoading && freeFrom > 0 && isFreeDelivery && (
                    <p className="text-xs text-muted-foreground">You’ve unlocked free delivery 🎉</p>
                  )}
                </div>

                <Separator />

                <div className="flex justify-between font-heading font-bold text-lg">
                  <span>Total</span>
                  <span className="flex items-center text-primary">
                    <IndianRupee className="h-5 w-5" />
                    {shopLoading ? '...' : grandTotal}
                  </span>
                </div>

                <Button
                  size="lg"
                  className="w-full rounded-full btn-glow"
                  onClick={() => navigate(`/shop/${shopSlug}/checkout`)}
                  disabled={shopLoading}
                  data-testid="checkout-btn"
                >
                  {shopLoading ? 'Loading charges...' : 'Proceed to Checkout'}
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    cart.forEach((item) => removeFromCart(item.id));
                    toast.success('Cart cleared');
                  }}
                >
                  Clear Cart
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}